import React, { useState, useEffect } from 'react';
import { X, Plus, Trash2, User, Shield, UserCheck, Edit, Mail, Copy, Key, ToggleLeft, ToggleRight, Check } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { dashboardAPI } from '../services/api';

const PORTAL_URL = 'https://saleshub.amgsol.com';

const UserManagement = ({ isOpen, onClose, inline = false }) => {
  const { getAllUsers, addUser, updateUser, deleteUser, isAdmin, refreshUsers, user } = useAuth();
  const [users, setUsers] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [changingPassword, setChangingPassword] = useState(null);
  const [newUser, setNewUser] = useState({
    username: '',
    name: '',
    email: '',
    password: '',
    role: 'bde'
  });
  const [editForm, setEditForm] = useState({
    email: '',
    password: '',
    email_sender_slot: ''
  });
  const [passwordForm, setPasswordForm] = useState({
    newPassword: '',
    confirmPassword: ''
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [copiedUserId, setCopiedUserId] = useState(null);
  const [emailSenders, setEmailSenders] = useState([]); // [{ slot, email, from }, ...] for dropdown labels
  // { enabled, domains } — users on these domains send from their OWN Microsoft
  // 365 mailbox, which makes the 1-4 account list a fallback rather than the
  // thing that decides who the mail comes from.
  const [graphInfo, setGraphInfo] = useState({ enabled: false, domains: [] });

  // Load users and email senders when modal opens
  useEffect(() => {
    if (isOpen) {
      loadUsers();
      dashboardAPI.getEmailSenders().then((res) => {
        const list = res?.data || res || [];
        setEmailSenders(Array.isArray(list) ? list : []);
        if (res?.graph) setGraphInfo(res.graph);
      }).catch(() => setEmailSenders([]));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      if (refreshUsers) {
        const fetchedUsers = await refreshUsers();
        if (fetchedUsers && Array.isArray(fetchedUsers)) {
          setUsers(fetchedUsers);
        } else {
          setUsers(getAllUsers());
        }
      } else {
        setUsers(getAllUsers());
      }
    } catch (error) {
      console.error('Error loading users:', error);
      setUsers(getAllUsers());
    } finally {
      setLoading(false);
    }
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    if (!newUser.username || !newUser.name) {
      setError('Please fill in all fields');
      setLoading(false);
      return;
    }

    if (users.find(u => u.username === newUser.username)) {
      setError('Username already exists');
      setLoading(false);
      return;
    }

    try {
      const result = await addUser(newUser);
      if (result.success) {
        await loadUsers();
        setNewUser({ username: '', name: '', email: '', password: '', role: 'bde' });
        setShowAddForm(false);
        setSuccess('User added successfully!');
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(result.error || 'Failed to add user');
      }
    } catch (error) {
      setError(error.message || 'Failed to add user');
    } finally {
      setLoading(false);
    }
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setEditForm({
      email: user.email || '',
      password: '',
      email_sender_slot: user.email_sender_slot != null && user.email_sender_slot !== '' ? String(user.email_sender_slot) : ''
    });
    setChangingPassword(null);
    setError('');
    setSuccess('');
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    const updateData = {};
    if (editForm.email) updateData.email = editForm.email;
    if (editForm.password) updateData.password = editForm.password;
    if (editForm.email_sender_slot !== undefined) updateData.email_sender_slot = editForm.email_sender_slot === '' ? null : parseInt(editForm.email_sender_slot, 10);

    if (Object.keys(updateData).length === 0) {
      setError('Change at least one field (email, password, or email sender account)');
      setLoading(false);
      return;
    }

    try {
      const result = await updateUser(editingUser.id, updateData);
      if (result.success) {
        await loadUsers();
        setEditingUser(null);
        setEditForm({ email: '', password: '', email_sender_slot: '' });
        setSuccess('User updated successfully!');
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(result.error || 'Failed to update user');
      }
    } catch (error) {
      setError(error.message || 'Failed to update user');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = (userItem) => {
    setChangingPassword(userItem);
    setPasswordForm({ newPassword: '', confirmPassword: '' });
    setEditingUser(null);
    setError('');
    setSuccess('');
  };

  const handleSavePassword = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!passwordForm.newPassword) {
      setError('Please enter a new password');
      return;
    }

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (passwordForm.newPassword.length < 4) {
      setError('Password must be at least 4 characters');
      return;
    }

    setLoading(true);

    try {
      const result = await updateUser(changingPassword.id, { password: passwordForm.newPassword });
      if (result.success) {
        const handed = `Sales Hub: ${PORTAL_URL}\nEmail: ${changingPassword.email || changingPassword.username || ''}\nPassword: ${passwordForm.newPassword}`;
        let copied = false;
        try { await navigator.clipboard.writeText(handed); copied = true; } catch { /* clipboard blocked — the admin still has what they typed */ }
        await loadUsers();
        setChangingPassword(null);
        setPasswordForm({ newPassword: '', confirmPassword: '' });
        setSuccess(`Password changed for ${changingPassword.name}!${copied ? ' Credentials copied to your clipboard.' : ''}`);
        setTimeout(() => setSuccess(''), 5000);
      } else {
        setError(result.error || 'Failed to change password');
      }
    } catch (error) {
      setError(error.message || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (userItem) => {
    setLoading(true);
    setError('');
    
    try {
      const newStatus = userItem.is_active === false ? true : false;
      const result = await updateUser(userItem.id, { is_active: newStatus });
      
      if (result.success) {
        await loadUsers();
        setSuccess(`User ${newStatus ? 'activated' : 'deactivated'} successfully!`);
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(result.error || 'Failed to update user status');
      }
    } catch (error) {
      setError(error.message || 'Failed to update user status');
    } finally {
      setLoading(false);
    }
  };

  // Passwords are stored as one-way bcrypt hashes — there is nothing readable to
  // copy, and the hash is NOT a password (pasting it into a login box fails, and
  // pasting it into "Change password" sets the password TO that gibberish).
  // So this copies the sign-in address only; to hand someone a password, use
  // "Change password", which copies the credentials you just typed.
  const handleCopyCredentials = async (userItem) => {
    const loginEmail = userItem.email || userItem.username || '';
    const credentials = `Sales Hub: ${PORTAL_URL}\nEmail: ${loginEmail}\nPassword: (not readable — use "Change password" to set one)`;
    
    try {
      await navigator.clipboard.writeText(credentials);
      setCopiedUserId(userItem.id);
      setSuccess(`Sign-in address copied for ${userItem.name} — use \u201cChange password\u201d to set a password.`);
      setTimeout(() => {
        setCopiedUserId(null);
        setSuccess('');
      }, 2000);
    } catch (error) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = credentials;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopiedUserId(userItem.id);
      setSuccess(`Sign-in address copied for ${userItem.name} — use \u201cChange password\u201d to set a password.`);
      setTimeout(() => {
        setCopiedUserId(null);
        setSuccess('');
      }, 2000);
    }
  };

  const handleDeleteUser = async (userId, username) => {
    if (window.confirm(`Are you sure you want to delete user "${username}"?`)) {
      setLoading(true);
      setError('');
      setSuccess('');
      
      try {
        const result = await deleteUser(userId);
        if (result.success) {
          await loadUsers();
          setSuccess('User deleted successfully!');
        } else {
          setError(result.error || 'Failed to delete user');
        }
      } catch (error) {
        setError(error.message || 'Failed to delete user');
      } finally {
        setLoading(false);
      }
    }
  };

  const getRoleIcon = (role) => {
    return role === 'admin' ? <Shield size={16} className="text-red-600" /> : <UserCheck size={16} className="text-blue-600" />;
  };

  const getRoleBadge = (role) => {
    return role === 'admin' 
      ? 'bg-red-100 text-red-800' 
      : 'bg-blue-100 text-blue-800';
  };

  const getStatusBadge = (isActive) => {
    if (isActive === false) {
      return 'bg-gray-100 text-gray-600';
    }
    return 'bg-green-100 text-green-800';
  };

  if (!isOpen) return null;

  if (inline) {
    return (
      <div className="bg-white rounded-lg w-full max-w-5xl mx-auto flex flex-col min-h-0 flex-1">
        <div className="flex-shrink-0 border-b border-gray-200 p-4 flex items-center justify-between bg-white sticky top-0 z-10">
          <button type="button" onClick={onClose} className="flex items-center gap-2 text-gray-600 hover:text-gray-900 font-medium">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
            Back
          </button>
          <h2 className="text-xl font-bold">User Management</h2>
          <div className="w-20" />
        </div>
        <div className="p-6 overflow-y-auto flex-1 min-h-0">
          {!isAdmin() && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <p className="text-red-800">Access denied. Only administrators can manage users.</p>
            </div>
          )}
          {isAdmin() && (
            <>
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold">Current Users ({users.length})</h3>
                <button
                  onClick={() => setShowAddForm(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-[#679632] text-white rounded-lg hover:bg-[#587f2b]"
                >
                  <Plus size={20} />
                  Add User
                </button>
              </div>
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                  <p className="text-red-800">{error}</p>
                </div>
              )}
              {success && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                  <p className="text-green-800">{success}</p>
                </div>
              )}
              {showAddForm && (
                <div className="bg-gray-50 rounded-lg p-4 mb-6">
                  <h4 className="font-semibold mb-4">Add New User</h4>
                  <form onSubmit={handleAddUser}>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Username *</label>
                        <input type="text" value={newUser.username} onChange={(e) => setNewUser({...newUser, username: e.target.value})} className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#679632] focus:border-transparent" placeholder="e.g., john" required />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
                        <input type="text" value={newUser.name} onChange={(e) => setNewUser({...newUser, name: e.target.value})} className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#679632] focus:border-transparent" placeholder="e.g., John Doe" required />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                        <input type="email" value={newUser.email} onChange={(e) => setNewUser({...newUser, email: e.target.value})} className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#679632] focus:border-transparent" placeholder="e.g., john@example.com" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
                        <input type="password" value={newUser.password} onChange={(e) => setNewUser({...newUser, password: e.target.value})} className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#679632] focus:border-transparent" placeholder="Leave blank for default" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                        <select value={newUser.role} onChange={(e) => setNewUser({...newUser, role: e.target.value})} className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#679632] focus:border-transparent">
                          <option value="bde">BDE</option>
                          <option value="admin">Admin</option>
                        </select>
                      </div>
                    </div>
                    <div className="flex gap-2 mt-4">
                      <button type="submit" disabled={loading} className="px-4 py-2 bg-[#679632] text-white rounded-lg hover:bg-[#587f2b] disabled:opacity-50">Add User</button>
                      <button type="button" onClick={() => setShowAddForm(false)} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
                    </div>
                  </form>
                </div>
              )}
              {loading ? (
                <div className="flex justify-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#679632]"></div></div>
              ) : (
                <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                  <table className="min-w-full">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">User</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Email</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Role</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Status</th>
                        <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {users.map((userItem) => (
                        <tr key={userItem.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-full bg-[#679632]/10 flex items-center justify-center text-[#679632] font-semibold text-sm flex-shrink-0">
                                {(userItem.name || 'U').charAt(0).toUpperCase()}
                              </div>
                              <div>
                                <p className="text-sm font-medium text-gray-900">{userItem.name}</p>
                                <p className="text-xs text-gray-500">{userItem.username}</p>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600">{userItem.email || '-'}</td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${getRoleBadge(userItem.role)}`}>
                              {getRoleIcon(userItem.role)}
                              {userItem.role === 'admin' ? 'Admin' : 'BDE'}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${getStatusBadge(userItem.is_active)}`}>
                              {userItem.is_active !== false ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center justify-end gap-1">
                              <button type="button" onClick={() => handleEditUser(userItem)} className="inline-flex items-center gap-1 px-2 py-1 text-xs text-gray-600 hover:bg-gray-100 rounded" title="Edit">
                                <Edit size={13} /> Edit
                              </button>
                              <button type="button" onClick={() => setChangingPassword(userItem)} className="inline-flex items-center gap-1 px-2 py-1 text-xs text-gray-600 hover:bg-gray-100 rounded" title="Change password">
                                <Key size={13} /> Password
                              </button>
                              <button type="button" onClick={() => handleCopyCredentials(userItem)} className={`inline-flex items-center gap-1 px-2 py-1 text-xs rounded ${copiedUserId === userItem.id ? 'text-green-600 bg-green-50' : 'text-gray-600 hover:bg-gray-100'}`} title="Copy credentials">
                                {copiedUserId === userItem.id ? <Check size={13} /> : <Copy size={13} />}
                                {copiedUserId === userItem.id ? 'Copied' : 'Copy'}
                              </button>
                              <button type="button" onClick={() => handleToggleActive(userItem)} className={`inline-flex items-center gap-1 px-2 py-1 text-xs rounded ${userItem.is_active !== false ? 'text-amber-600 hover:bg-amber-50' : 'text-green-600 hover:bg-green-50'}`}>
                                {userItem.is_active !== false ? <ToggleRight size={13} /> : <ToggleLeft size={13} />}
                                {userItem.is_active !== false ? 'Deactivate' : 'Activate'}
                              </button>
                              <button type="button" onClick={() => handleDeleteUser(userItem.id, userItem.username)} className="inline-flex items-center gap-1 px-2 py-1 text-xs text-red-500 hover:bg-red-50 rounded" title="Delete">
                                <Trash2 size={13} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {(editingUser || changingPassword) && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
                  <div className="bg-white rounded-lg max-w-md w-full p-6">
                    {editingUser && (
                      <>
                        <h3 className="text-lg font-semibold mb-4">Edit {editingUser.name}</h3>
                        <div className="space-y-4">
                          <div><label className="block text-sm font-medium text-gray-700 mb-1">Email</label><input type="email" value={editForm.email} onChange={(e) => setEditForm({...editForm, email: e.target.value})} className="w-full border border-gray-300 rounded-lg px-3 py-2" /></div>
                          <div><label className="block text-sm font-medium text-gray-700 mb-1">New password (optional)</label><input type="password" value={editForm.password} onChange={(e) => setEditForm({...editForm, password: e.target.value})} className="w-full border border-gray-300 rounded-lg px-3 py-2" placeholder="Leave blank to keep current" /></div>
                          {(() => {
                            // Anyone on a tenant domain sends as THEMSELVES via
                            // Microsoft 365, so say so instead of letting the
                            // old 1-4 account list imply otherwise.
                            const addr = (editForm.email || editingUser.email || '').trim().toLowerCase();
                            const domain = addr.includes('@') ? addr.split('@')[1] : '';
                            const ownMailbox = graphInfo.enabled && graphInfo.domains.includes(domain);
                            return (
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                  {ownMailbox ? 'Fallback email account' : 'Email sender account'}
                                </label>
                                {ownMailbox && (
                                  <div className="mb-2 text-xs bg-[#eaf3e0] text-[#3f5e1c] border border-[#cfe3b6] rounded-lg px-3 py-2">
                                    Sends from their own mailbox <span className="font-semibold">{editForm.email || editingUser.email}</span> via Microsoft 365,
                                    so replies come back to them. The account below is only used if that mailbox is unavailable.
                                  </div>
                                )}
                                <select value={editForm.email_sender_slot} onChange={(e) => setEditForm({...editForm, email_sender_slot: e.target.value})} className="w-full border border-gray-300 rounded-lg px-3 py-2">
                                  <option value="">None (use first configured)</option>
                                  {[1, 2, 3, 4].map((slot) => {
                                    const sender = emailSenders.find((s) => s.slot === slot);
                                    const label = sender?.email ? `Account ${slot} (${sender.email})` : `Account ${slot}`;
                                    return <option key={slot} value={String(slot)}>{label}</option>;
                                  })}
                                </select>
                                <p className="text-xs text-gray-500 mt-1">
                                  {ownMailbox
                                    ? 'Only used as a fallback — normal sending goes out from their own mailbox.'
                                    : 'Which email account this user sends from in Send Emails (1–4).'}
                                </p>
                              </div>
                            );
                          })()}
                        </div>
                        <div className="flex gap-2 mt-6">
                          <button type="button" onClick={(e) => handleUpdateUser(e)} disabled={loading} className="px-4 py-2 bg-[#679632] text-white rounded-lg disabled:opacity-50">Save</button>
                          <button type="button" onClick={() => { setEditingUser(null); setEditForm({ email: '', password: '', email_sender_slot: '' }); }} className="px-4 py-2 border border-gray-300 rounded-lg">Cancel</button>
                        </div>
                      </>
                    )}
                    {changingPassword && !editingUser && (
                      <>
                        <h3 className="text-lg font-semibold mb-4">Change password for {changingPassword.name}</h3>
                        <div className="space-y-4">
                          <div><label className="block text-sm font-medium text-gray-700 mb-1">New password</label><input type="password" value={passwordForm.newPassword} onChange={(e) => setPasswordForm({...passwordForm, newPassword: e.target.value})} className="w-full border border-gray-300 rounded-lg px-3 py-2" /></div>
                          <div><label className="block text-sm font-medium text-gray-700 mb-1">Confirm password</label><input type="password" value={passwordForm.confirmPassword} onChange={(e) => setPasswordForm({...passwordForm, confirmPassword: e.target.value})} className="w-full border border-gray-300 rounded-lg px-3 py-2" /></div>
                        </div>
                        <div className="flex gap-2 mt-6">
                          <button type="button" onClick={handleSavePassword} disabled={loading} className="px-4 py-2 bg-[#679632] text-white rounded-lg disabled:opacity-50">{loading ? 'Saving\u2026' : 'Update'}</button>
                          <button type="button" onClick={() => { setChangingPassword(null); setPasswordForm({ newPassword: '', confirmPassword: '' }); }} className="px-4 py-2 border border-gray-300 rounded-lg">Cancel</button>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-5xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold">User Management</h2>
            <button 
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <X size={24} />
            </button>
          </div>

          {!isAdmin() && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <p className="text-red-800">Access denied. Only administrators can manage users.</p>
            </div>
          )}

          {isAdmin() && (
            <>
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold">Current Users ({users.length})</h3>
                <button
                  onClick={() => setShowAddForm(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-[#679632] text-white rounded-lg hover:bg-[#587f2b]"
                >
                  <Plus size={20} />
                  Add User
                </button>
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                  <p className="text-red-800">{error}</p>
                </div>
              )}

              {success && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                  <p className="text-green-800">{success}</p>
                </div>
              )}

              {showAddForm && (
                <div className="bg-gray-50 rounded-lg p-4 mb-6">
                  <h4 className="font-semibold mb-4">Add New User</h4>
                  <form onSubmit={handleAddUser}>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Username *
                        </label>
                        <input
                          type="text"
                          value={newUser.username}
                          onChange={(e) => setNewUser({...newUser, username: e.target.value})}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#679632] focus:border-transparent"
                          placeholder="e.g., john"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Full Name *
                        </label>
                        <input
                          type="text"
                          value={newUser.name}
                          onChange={(e) => setNewUser({...newUser, name: e.target.value})}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#679632] focus:border-transparent"
                          placeholder="e.g., John Doe"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Email
                        </label>
                        <input
                          type="email"
                          value={newUser.email}
                          onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#679632] focus:border-transparent"
                          placeholder="e.g., john@company.com"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Password
                        </label>
                        <input
                          type="password"
                          value={newUser.password}
                          onChange={(e) => setNewUser({...newUser, password: e.target.value})}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#679632] focus:border-transparent"
                          placeholder="Leave empty for default (username123)"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Role
                        </label>
                        <select
                          value={newUser.role}
                          onChange={(e) => setNewUser({...newUser, role: e.target.value})}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#679632] focus:border-transparent"
                        >
                          <option value="bde">BDE User</option>
                          <option value="admin">Admin</option>
                        </select>
                      </div>
                    </div>
                    <div className="flex gap-3 mt-4">
                      <button
                        type="submit"
                        disabled={loading}
                        className="px-4 py-2 bg-[#679632] text-white rounded-lg hover:bg-[#587f2b] disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {loading ? 'Adding...' : 'Add User'}
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowAddForm(false)}
                        className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300"
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                </div>
              )}

              {editingUser && (
                <div className="bg-gray-50 rounded-lg p-4 mb-6">
                  <h4 className="font-semibold mb-4">Edit User: {editingUser.name}</h4>
                  <form onSubmit={handleUpdateUser}>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Email
                        </label>
                        <input
                          type="email"
                          value={editForm.email}
                          onChange={(e) => setEditForm({...editForm, email: e.target.value})}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#679632] focus:border-transparent"
                          placeholder="e.g., user@company.com"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          New Password
                        </label>
                        <input
                          type="password"
                          value={editForm.password}
                          onChange={(e) => setEditForm({...editForm, password: e.target.value})}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#679632] focus:border-transparent"
                          placeholder="Leave empty to keep current password"
                        />
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Email sender account
                        </label>
                        <select
                          value={editForm.email_sender_slot}
                          onChange={(e) => setEditForm({...editForm, email_sender_slot: e.target.value})}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#679632] focus:border-transparent"
                        >
                          <option value="">None (use first configured)</option>
                          {[1, 2, 3, 4].map((slot) => {
                            const sender = emailSenders.find((s) => s.slot === slot);
                            const label = sender?.email ? `Account ${slot} (${sender.email})` : `Account ${slot}`;
                            return <option key={slot} value={String(slot)}>{label}</option>;
                          })}
                        </select>
                        <p className="text-xs text-gray-500 mt-1">Which email account this user sends from (1–4).</p>
                      </div>
                    </div>
                    <div className="flex gap-3 mt-4">
                      <button
                        type="submit"
                        disabled={loading}
                        className="px-4 py-2 bg-[#679632] text-white rounded-lg hover:bg-[#587f2b] disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {loading ? 'Updating...' : 'Update User'}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setEditingUser(null);
                          setEditForm({ email: '', password: '', email_sender_slot: '' });
                        }}
                        className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300"
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                </div>
              )}

              {changingPassword && (
                <div className="bg-yellow-50 rounded-lg p-4 mb-6 border border-yellow-200">
                  <h4 className="font-semibold mb-4 text-yellow-800">Change Password: {changingPassword.name}</h4>
                  <form onSubmit={handleSavePassword}>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          New Password *
                        </label>
                        <input
                          type="password"
                          value={passwordForm.newPassword}
                          onChange={(e) => setPasswordForm({...passwordForm, newPassword: e.target.value})}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                          placeholder="Enter new password"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Confirm Password *
                        </label>
                        <input
                          type="password"
                          value={passwordForm.confirmPassword}
                          onChange={(e) => setPasswordForm({...passwordForm, confirmPassword: e.target.value})}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                          placeholder="Confirm new password"
                          required
                        />
                      </div>
                    </div>
                    <div className="flex gap-3 mt-4">
                      <button
                        type="submit"
                        disabled={loading}
                        className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {loading ? 'Saving...' : 'Save Password'}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setChangingPassword(null);
                          setPasswordForm({ newPassword: '', confirmPassword: '' });
                        }}
                        className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300"
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                </div>
              )}

              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Role</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Username</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {users.map((userItem) => (
                      <tr key={userItem.id} className={`hover:bg-gray-50 ${userItem.is_active === false ? 'opacity-60' : ''}`}>
                        <td className="px-4 py-2">
                          <div className="flex items-center gap-2">
                            <User size={20} className="text-gray-400" />
                            <span className="font-medium">{userItem.name}</span>
                          </div>
                        </td>
                        <td className="px-4 py-2">
                          <div className="flex items-center gap-2">
                            {getRoleIcon(userItem.role)}
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getRoleBadge(userItem.role)}`}>
                              {userItem.role === 'admin' ? 'Admin' : 'BDE'}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-2">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadge(userItem.is_active)}`}>
                            {userItem.is_active === false ? 'Inactive' : 'Active'}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-sm text-gray-600">{userItem.username}</td>
                        <td className="px-4 py-2">
                          <div className="flex items-center gap-1 text-sm text-gray-600">
                            {userItem.email ? (
                              <>
                                <Mail size={14} className="text-gray-400" />
                                <span>{userItem.email}</span>
                              </>
                            ) : (
                              <span className="text-gray-400">No email</span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-2">
                          <div className="flex items-center gap-1">
                            {/* Copy Credentials */}
                            <button
                              onClick={() => handleCopyCredentials(userItem)}
                              className={`p-1.5 rounded ${copiedUserId === userItem.id ? 'bg-green-100 text-green-600' : 'text-gray-500 hover:text-blue-600 hover:bg-blue-50'}`}
                              title="Copy email and password to share"
                            >
                              {copiedUserId === userItem.id ? <Check size={16} /> : <Copy size={16} />}
                            </button>

                            {/* Change Password */}
                            <button
                              onClick={() => handleChangePassword(userItem)}
                              className="p-1.5 rounded text-yellow-600 hover:text-yellow-800 hover:bg-yellow-50"
                              title="Change password"
                            >
                              <Key size={16} />
                            </button>

                            {/* Toggle Active/Inactive */}
                            {userItem.role !== 'admin' && (
                              <button
                                onClick={() => handleToggleActive(userItem)}
                                className={`p-1.5 rounded ${userItem.is_active === false ? 'text-gray-500 hover:text-green-600 hover:bg-green-50' : 'text-green-600 hover:text-gray-500 hover:bg-gray-100'}`}
                                title={userItem.is_active === false ? 'Activate user' : 'Deactivate user'}
                              >
                                {userItem.is_active === false ? <ToggleLeft size={18} /> : <ToggleRight size={18} />}
                              </button>
                            )}

                            {/* Edit User */}
                            {userItem.role !== 'admin' && (
                              <button
                                onClick={() => handleEditUser(userItem)}
                                className="p-1.5 rounded text-blue-600 hover:text-blue-800 hover:bg-blue-50"
                                title="Edit user"
                              >
                                <Edit size={16} />
                              </button>
                            )}

                            {/* Delete User */}
                            {userItem.role !== 'admin' && user && (
                              (user.name?.toLowerCase().includes('shree') ||
                               user.name?.toLowerCase().includes('siva') ||
                               user.name?.toLowerCase() === 'admin' ||
                               user.username?.toLowerCase() === 'admin' ||
                               user.name?.toLowerCase().includes('dileep')) && (
                                <button
                                  onClick={() => handleDeleteUser(userItem.id, userItem.name)}
                                  className="p-1.5 rounded text-red-600 hover:text-red-800 hover:bg-red-50"
                                  title="Delete user"
                                >
                                  <Trash2 size={16} />
                                </button>
                              )
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {users.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  No users found. Click "Add User" to create a new user.
                </div>
              )}

              <div className="mt-6 bg-blue-50 rounded-lg p-4">
                <h4 className="font-semibold text-blue-900 mb-2">Quick Actions</h4>
                <ul className="text-sm text-blue-800 space-y-1">
                  <li>📋 <strong>Copy</strong> - Copy email & password to share with user</li>
                  <li>🔑 <strong>Key</strong> - Change user's password</li>
                  <li>🔘 <strong>Toggle</strong> - Activate or deactivate user account</li>
                  <li>✏️ <strong>Edit</strong> - Edit user email or password</li>
                </ul>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserManagement;
